import {
  Box,
  Button,
  Grid2,
  InputLabel,
  Modal,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import crossIcon from "../../../assets/crossIcon.png.png";
import { BeerFormStateEdit } from "../../../constants/types";
import { uploadButton, uploadButtonContainer } from "./productStyles";

export interface productFormProps extends BeerFormStateEdit {
  buttonTextOne: string;
  buttonTextTwo: string;
  formTitle: string;
  isEdit: boolean;
  onSubmit: (data: BeerFormStateEdit) => void;
}

const StyledTextField = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "5px",
});

const StyledInput = styled("input")({
  paddingLeft: "5px",
  height: "5vh",
  width: "100%",
  border: "1px solid #c5ced6",
  borderRadius: "4px",
});

const FileTab = styled("div")({
  display: "flex",
  alignItems: "center",
  backgroundColor: "#EFEFEF",
  border: "2px solid #767676",
  padding: "5px",
  margin: "5px",
  fontSize: "12px",
  gap: "5px",
  height: "20px",
  overflow: "hidden",
  fontFamily: "Roboto",
});

const ProductForm: React.FC<productFormProps> = ({
  beerName,
  description,
  fileUpload,
  quantity,
  price,
  onSubmit,
  buttonTextOne,
  buttonTextTwo,
  formTitle,
  existingImages,
  isEdit,
  abv,
  ibu,
}) => {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [formData, setFormData] = useState<BeerFormStateEdit>({
    beerName: beerName || "",
    quantity: quantity || "",
    description: description || "",
    price: price || "",
    fileUpload: fileUpload || [],
    existingImages: existingImages || [],
    abv: abv || "",
    ibu: ibu || "",
  });
  const [fileCount, setFileCount] = useState(
    existingImages.length + fileUpload.length || 0
  );

  useEffect(() => {
    setFileCount(
      formData.existingImages.length + formData.fileUpload.length || 0
    );
  }, [formData.existingImages, formData.fileUpload]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFormData((prevData) => ({
        ...prevData,
        fileUpload: [...prevData.fileUpload, ...newFiles],
      }));
    }
  };

  const handleRemoveFile = (fileToRemove: File) => {
    setFormData((prevData) => ({
      ...prevData,
      fileUpload: prevData.fileUpload.filter((file) => file !== fileToRemove),
    }));
  };

  const handleRemoveFileUrl = (fileToRemove: any) => {
    setFormData((prevData) => ({
      ...prevData,
      existingImages: prevData.existingImages.filter(
        (file) => file.url !== fileToRemove.url
      ),
    }));
  };

  const handleReset = () => {
    setFormData({
      beerName: "",
      quantity: "",
      description: "",
      price: "",
      fileUpload: [],
      existingImages: [],
      abv: "",
      ibu: "",
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    handleClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };
  return (
    <div>
      <form
        style={{
          padding: "40px 20px",
          background: "white",
          display: "flex",
          alignItems: "start",
          justifyContent: "space-between",
          flexDirection: "column",
        }}
        onSubmit={handleSubmit}
      >
        <div>
          <Typography
            variant="h1"
            style={{
              fontSize: "18px",
              marginBottom: "3vh",
              fontFamily: "Montserrat",
            }}
          >
            {formTitle}
          </Typography>
        </div>
        <Grid2
          container
          spacing={2}
          marginTop={2}
          fontFamily={"Roboto"}
          sx={{
            fontFamily: "Roboto",
            pr: { md: 18 },
          }}
        >
          <Grid2 size={[12, 6, 4]}>
            <StyledTextField>
              <InputLabel
                htmlFor="beerName"
                sx={{ fontFamily: "Roboto", fontWeight: "500" }}
              >
                Beer Name
              </InputLabel>

              <StyledInput
                placeholder="Beer Name"
                type="text"
                id="beerName"
                data-testid="beerName1"
                name="beerName"
                value={formData.beerName}
                onChange={handleChange}
              />
            </StyledTextField>
          </Grid2>
          <Grid2 size={[12, 6, 4]}>
            <StyledTextField>
              <InputLabel
                htmlFor="abv"
                sx={{ fontFamily: "Roboto", fontWeight: "500" }}
              >
                ABV%
              </InputLabel>
              <StyledInput
                placeholder="Please Enter ABV value"
                type="number"
                id="abv"
                name="abv"
                value={formData.abv}
                onChange={handleChange}
                required
              />
            </StyledTextField>
          </Grid2>
          <Grid2 size={[12, 6, 4]}>
            <StyledTextField>
              <InputLabel
                htmlFor="ibu"
                sx={{ fontFamily: "Roboto", fontWeight: "500" }}
              >
                IBU
              </InputLabel>
              <StyledInput
                placeholder="Please Enter IBU value"
                type="number"
                id="ibu"
                name="ibu"
                value={formData.ibu}
                onChange={handleChange}
                required
              />
            </StyledTextField>
          </Grid2>
          <Grid2 size={[12, 6, 4]}>
            <StyledTextField>
              <InputLabel
                htmlFor="price"
                sx={{ fontFamily: "Roboto", fontWeight: "500" }}
              >
                Beer Price
              </InputLabel>
              <StyledInput
                placeholder="Beer Price"
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                required
              />
            </StyledTextField>
          </Grid2>
          <Grid2 size={12}>
            <InputLabel
              htmlFor="description"
              sx={{
                fontFamily: "Roboto",
                fontWeight: "500",
                marginBottom: "5px",
              }}
            >
              Description
            </InputLabel>
            <TextField
              multiline
              rows={4}
              placeholder="Description"
              data-testid="Description"
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              sx={{
                width: "100%",
                borderRadius: "4px",
                border: "1px solid #C5CED6",
                "& .MuiInputBase-input": {
                  color: "black",
                },
              }}
            />
          </Grid2>

          <Grid2 size={[12]}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
                gap: "5px",
              }}
            >
              <InputLabel
                htmlFor="UploadButton"
                sx={{ fontFamily: "Roboto", fontWeight: "500" }}
              >
                {isEdit ? "Uploaded Documents" : "Upload Documents"}
              </InputLabel>
              <input
                type="file"
                id={`UploadButton`}
                style={{ display: "none" }}
                data-testid="fileUpload"
                onChange={handleFileUpload}
              />
              <label htmlFor={`UploadButton`}>
                <div style={uploadButtonContainer}>
                  <div style={uploadButton}>Choose file</div>
                  {fileCount === 0 ? "No" : fileCount} file
                  {fileCount > 1 && "s"} chosen
                </div>
              </label>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "10px",
                  margin: "10px 0",
                  fontFamily: "Roboto",
                }}
              >
                {formData.fileUpload.map((file, index) => (
                  <FileTab key={index}>
                    <span>{file.name}</span>
                    <img
                      onClick={() => handleRemoveFile(file)}
                      alt="remove file"
                      src={crossIcon}
                      style={{ cursor: "pointer" }}
                    />
                  </FileTab>
                ))}
                {formData.existingImages.map((file, index) => (
                  <FileTab key={index}>
                    <span>{file.fileName}</span>
                    <img
                      onClick={() => handleRemoveFileUrl(file)}
                      alt="remove file"
                      src={crossIcon}
                      style={{ cursor: "pointer" }}
                    />
                  </FileTab>
                ))}
              </div>
              <Typography style={{ color: "#9095A1", marginBottom: "5vh" }}>
                Will be re-sized to 75px height. JPG, GIF, and PNG are accepted.
              </Typography>
            </div>
          </Grid2>
        </Grid2>

        <Grid2
          container
          spacing={2}
          display="flex"
          justifyContent="flex-end"
          padding={2}
          width={"100%"}
        >
          <Button
            sx={{
              fontFamily: "poppins",
              width: "15vw",
              height: "6vh",
              borderRadius: "16px",
            }}
            data-testid="reset-button"
            className="button"
            variant="outlined"
            onClick={handleOpen}
          >
            {buttonTextTwo}
          </Button>

          <Button
            type="submit"
            data-testid="submit-button"
            className="button"
            sx={{
              fontFamily: "poppins",
              width: "15vw",
              height: "6vh",
              borderRadius: "16px",
              boxShadow: "none",
              background: "#333333",
              "&:hover": {
                boxShadow: "none",
              },
            }}
            variant="contained"
          >
            {buttonTextOne}
          </Button>
        </Grid2>
      </form>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
      >
        <Box
          sx={{
            fontFamily: "poppins",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            boxShadow: 24,
            padding: "35px",
            borderRadius: 2,
          }}
        >
          <Typography
            id="simple-modal-title"
            variant="h3"
            component="h1"
            sx={{ margin: "10px 0 30px" }}
          >
            Are you sure you want to reset?
          </Typography>
          <Button
            variant="contained"
            onClick={handleReset}
            sx={{
              padding: "18px 32px",
              mt: 3,
              fontWeight: "600",
              borderRadius: "5px",
              background: "#333333",
              color: "white",
            }}
          >
            Yes, I Am Sure
          </Button>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              mt: 3,
              border: "1px solid black",
              borderRadius: "5px",
              padding: "18px 32px",
              fontWeight: "600",
            }}
          >
            Cancel
          </Button>
        </Box>
      </Modal>
    </div>
  );
};

export default ProductForm;
